import { useEffect, useRef } from 'react';

import { useMainCta, type MainCtaConfig } from '@/contexts/mainCta';

/**
 * Register a primary call-to-action for the current screen. The CTA is
 * rendered in-document by `<MainCta />` inside `AppShell`, sitting above the
 * `<TabBar />`. The hook auto-clears on unmount so each route can declare
 * its own CTA without collisions.
 *
 * Pass `null` to register NO CTA — for screens where the button only makes
 * sense in some states. `enabled: false` is not a substitute: it greys the bar
 * out but still renders it (`<MainCta />` only checks `cta !== null`), which
 * leaves a dead button on screens that have no action at all.
 *
 * `onClick` is wrapped in a ref so an unstable callback identity (common when
 * the route does not memoize via `useCallback`) does not retrigger the
 * register/unregister effect — which would loop with the context state update.
 */
export function useMainButton(config: MainCtaConfig | null) {
  const { setCta } = useMainCta();
  const onClickRef = useRef(config?.onClick);
  onClickRef.current = config?.onClick;

  // Read the primitives out so the effect deps stay value-based: passing an
  // inline object literal (every existing caller does) would otherwise give a
  // new identity each render and loop against the context state update.
  const text = config?.text;
  const enabled = config?.enabled ?? true;
  const loading = config?.loading ?? false;

  useEffect(() => {
    if (text === undefined) {
      setCta(null);
      return;
    }
    setCta({
      text,
      enabled,
      loading,
      onClick: () => onClickRef.current?.(),
    });
    return () => setCta(null);
  }, [text, enabled, loading, setCta]);
}
